/**
 * "View as" — the 3-view lens (design_handoff_view_switcher).
 *
 * CORE PRINCIPLE: this is a VIEW, not access control. In Repsity anyone can do
 * anything (a broker can also own property; an attorney can also be a buyer).
 * The view only reconfigures what's *surfaced by default* — it must never be the
 * mechanism that grants or denies capability. Server-side authorization is a
 * separate concern and is unaffected by this.
 *
 * 60+ DB roles roll up to 9 canonical roles, which roll up to these 3 views.
 * The switcher operates entirely at the 3-view level; it never sees DB roles.
 */

export type RepsityView = "brokerage" | "provider" | "customer";

export type CanonicalRole =
  | "BROKER"
  | "ADMIN"
  | "AGENT"
  | "ATTORNEY"
  | "SERVICE_PROVIDER"
  | "BUYER"
  | "SELLER"
  | "TENANT"
  | "LANDLORD";

export const VIEW_ROLES: Record<RepsityView, CanonicalRole[]> = {
  brokerage: ["BROKER", "ADMIN", "AGENT"],
  provider: ["ATTORNEY", "SERVICE_PROVIDER"],
  customer: ["BUYER", "SELLER", "TENANT", "LANDLORD"],
};

/** Display order for the switcher menu (senior → junior). */
export const VIEW_ORDER: RepsityView[] = ["brokerage", "provider", "customer"];

export interface ViewMeta {
  view: RepsityView;
  /** "Brokerage" */
  label: string;
  /** "Broker · Admin · Agent" — the rolled-up roles, for the trigger/menu. */
  roleLine: string;
  /** One-line sentence-case blurb shown in the menu. */
  blurb: string;
  /** Tailwind bg utility for the accent dot (the only per-view color cue). */
  dotClass: string;
}

export const VIEW_META: Record<RepsityView, ViewMeta> = {
  brokerage: {
    view: "brokerage",
    label: "Brokerage",
    roleLine: "Broker · Admin · Agent",
    blurb: "Run deals, listings, and your team.",
    dotClass: "bg-brand",
  },
  provider: {
    view: "provider",
    label: "Provider",
    roleLine: "Attorney · Service Provider",
    blurb: "Referrals and engagements you're servicing.",
    dotClass: "bg-status-ready",
  },
  customer: {
    view: "customer",
    label: "Customer",
    roleLine: "Buyer · Seller · Tenant · Landlord",
    blurb: "Your properties, deals, and searches.",
    dotClass: "bg-secondary-green",
  },
};

/** One localStorage key + one custom event — the single read/write seam. A
 *  server-side user preference can replace the storage impl later without
 *  touching any consumer (see the handoff "Future" section). */
export const VIEW_STORAGE_KEY = "repsity:viewAs";
export const VIEW_CHANGE_EVENT = "repsity:viewchange";

/** Fallback when unset/invalid. In production this could seed from the user's
 *  most-senior canonical role (Brokerage > Provider > Customer); any user can
 *  still switch freely. */
export const DEFAULT_VIEW: RepsityView = "brokerage";

export function isRepsityView(v: unknown): v is RepsityView {
  return v === "brokerage" || v === "provider" || v === "customer";
}

/** Read the persisted view, validating against the 3 known values. */
export function readStoredView(): RepsityView {
  if (typeof window === "undefined") return DEFAULT_VIEW;
  const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return isRepsityView(raw) ? raw : DEFAULT_VIEW;
}
